import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Checkbox, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthProvider";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { INK, INK_BLACK, NEUTRAL, STATUS, SURFACE } from "@/theme/tokens";
import type { DepartmentLocation, OpenShift, TransformedShift } from "@/utils/models";
import { ClickableLocationAddress } from "./ClickableLocationAddress";

type MedicalDayCardProps = {
	dateKey: string;
	currentDate: DateTime;
	isExpanded: boolean;
	summary: {
		shiftCount: number;
		requestCount: number;
		sections: string[];
	};
	dateRequests: OpenShift[];
	shiftsBySection: Record<string, TransformedShift[]>;
	onToggle: (dateKey: string) => void;
	onCheckBoxChanged?: (requestId: string, checked: boolean) => void;
	requestIdsToPick: string[];
};

type CombinedItemType =
	| { type: "shift"; data: TransformedShift; departmentLocation?: DepartmentLocation }
	| { type: "request"; data: OpenShift; departmentLocation?: DepartmentLocation };

type MedicalDayExpandedContentProps = {
	summary: {
		shiftCount: number;
		requestCount: number;
	};
	aggregatedBySection: Record<string, Array<CombinedItemType>>;
	isRequestPending: (request: OpenShift) => boolean;
	onCheckBoxChanged?: (requestId: string, checked: boolean) => void;
	requestIdsToPick: string[];
	shouldDisplayLocation: boolean;
};

const MedicalDayExpandedContent: FC<MedicalDayExpandedContentProps> = ({
	summary,
	aggregatedBySection,
	isRequestPending,
	onCheckBoxChanged,
	requestIdsToPick,
	shouldDisplayLocation,
}) => {
	const { t } = useTranslation();
	if (!(summary.shiftCount > 0 || summary.requestCount > 0)) {
		return null;
	}

	return (
		<Box sx={{ pl: 1, pt: 1, bgcolor: NEUTRAL[50] }}>
			{/* Aggregated shifts and requests grouped by section */}
			{Object.entries(aggregatedBySection).map(([section, items]) => (
				<Box key={section} sx={{ mb: 2 }}>
					<Typography
						sx={(theme) => ({
							color: INK.SECONDARY,
							fontWeight: 600,
							fontSize: "12px",
							mb: 1,
							bgcolor: alpha(theme.palette.primary.main, 0.2),
							borderRadius: 0.75,
							px: 1,
						})}
					>
						{section === "Unassigned" ? t("unassigned") : section}
					</Typography>
					{items.map((item, idx) => {
						const requestPending = item.type === "request" ? isRequestPending(item.data) : false;
						const isCheckboxChecked = requestPending ? true : item.data.id && requestIdsToPick ? requestIdsToPick.includes(item.data.id) : undefined;
						const itemDepartmentAddress = item.departmentLocation?.address;
						return (
							<Box
								key={item.data.id || idx}
								sx={{
									p: 1.5,
									ml: 2,
									mb: 1,
									bgcolor: SURFACE,
									borderRadius: "8px",
									fontSize: "14px",
								}}
							>
								{item.type === "request" ? (
									<Box>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												color: INK.SECONDARY,
												fontSize: "inherit",
											}}
										>
											<Checkbox
												checked={isCheckboxChecked}
												disabled={requestPending}
												onChange={(e) => {
													if (onCheckBoxChanged && item.data.id) {
														onCheckBoxChanged(item.data.id, e.target.checked);
													}
												}}
											/>
											{shouldDisplayLocation && `${item.departmentLocation?.location || t("unassigned.location")} • `}
											<Typography
												sx={{
													ml: 0.5,
													px: 1,
													background: alpha(STATUS.WARNING.main, 0.4),
													color: INK_BLACK,
													textAlign: "center",
													borderRadius: "8px",
													fontSize: "12px",
												}}
											>
												{requestPending ? t("pending") : t("opportunity")}
											</Typography>
										</Box>
										{shouldDisplayLocation && <ClickableLocationAddress address={itemDepartmentAddress} />}
									</Box>
								) : (
									shouldDisplayLocation && (
										<Box>
											<Typography
												sx={(theme) => ({
													color: theme.palette.primary.main,
													fontSize: "inherit",
												})}
											>
												{item.data.location}
											</Typography>
											<ClickableLocationAddress pl={0} address={itemDepartmentAddress} />
										</Box>
									)
								)}
							</Box>
						);
					})}
				</Box>
			))}
		</Box>
	);
};

export const MedicalDayCard: FC<MedicalDayCardProps> = ({
	dateKey,
	currentDate,
	isExpanded,
	summary,
	dateRequests,
	shiftsBySection,
	onToggle,
	onCheckBoxChanged,
	requestIdsToPick,
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const { t } = useTranslation();
	const nurseId = userInfo?.nurses_info?.at(0)?.nurse.id;
	const shouldDisplayLocation = useFeatureFlag(FEATURE_FLAGS.ShouldDisplayLocationInStaffApp);

	const aggregatedBySection = useMemo(() => {
		// Aggregate shifts and requests by section
		const combined: Record<string, Array<CombinedItemType>> = {
			...Object.fromEntries(Object.keys(shiftsBySection).map((section) => [section, []])),
		};

		Object.entries(shiftsBySection).forEach(([section, sectionShifts]) => {
			// Add shifts to their sections
			combined[section] = sectionShifts.map<CombinedItemType>((shift) => {
				const locationInfo = chosenDepartment.locations.find((loc) => loc.location === shift.location);
				return {
					type: "shift" as const,
					data: shift,
					departmentLocation: locationInfo,
				};
			});
		});

		dateRequests.forEach((request) => {
			// Add requests to their sections
			const locationInfo = chosenDepartment.locations.find((loc) => loc.location === request.location);
			const section = locationInfo?.section || t("unassigned.location");

			if (!combined[section]) {
				combined[section] = [];
			}
			combined[section].push({ type: "request" as const, data: request, departmentLocation: locationInfo });
		});

		return combined;
	}, [shiftsBySection, dateRequests, chosenDepartment.locations, t]);

	const isRequestPending = (request: OpenShift): boolean => {
		return (request.request_data?.filter((reqData) => reqData.nurse_id === nurseId)?.length || 0) > 0;
	};

	return (
		<Box id={`shift-${dateKey}`} data-schedule-day={dateKey}>
			<Paper elevation={0}>
				{/* Day Header - always visible */}
				<Box
					onClick={() => onToggle(dateKey)}
					sx={{
						p: 1.5,
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "start",
							alignItems: "start",
							gap: 1,
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "start",
								alignItems: "center",
								gap: 1,
								width: "20px",
								pt: "2px",
							}}
						>
							{summary.shiftCount > 0 && (
								<Box
									sx={(theme) => ({
										bgcolor: alpha(theme.palette.primary.main, 0.2),
										color: INK.SECONDARY,
										borderRadius: "50%",
										width: "20px",
										height: "20px",
										fontSize: "14px",
										textAlign: "center",
										lineHeight: "20px",
										fontWeight: 700,
									})}
								>
									{summary.shiftCount}
								</Box>
							)}
						</Box>

						<Box>
							<Typography sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "14px" }}>{currentDate.toFormat("ccc, MMM d")}</Typography>
							{summary.sections.length > 0 && (
								<Typography sx={{ color: INK.SECONDARY, fontSize: "12px", fontWeight: 600 }}>{summary.sections.join(", ")}</Typography>
							)}
						</Box>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						{summary.requestCount > 0 && (
							<Box
								sx={{
									width: "96px",
									background: alpha(STATUS.WARNING.main, 0.4),
									textAlign: "center",
									borderRadius: "8px",
								}}
							>
								<Typography
									sx={{
										color: INK_BLACK,
										fontSize: "12px",
									}}
								>
									{`${summary.requestCount} ${summary.requestCount > 1 ? t("opportunities") : t("opportunity")}`}
								</Typography>
							</Box>
						)}
						{(!!summary.requestCount || !!summary.shiftCount) && (
							<KeyboardArrowRightIcon
								sx={(theme) => ({
									fontSize: 20,
									color: theme.palette.primary.main,
									transition: "transform 0.2s ease",
									transform: isExpanded ? "rotate(-90deg)" : "rotate(90deg)",
								})}
							/>
						)}
					</Box>
				</Box>
			</Paper>
			{isExpanded && (
				<MedicalDayExpandedContent
					summary={summary}
					aggregatedBySection={aggregatedBySection}
					isRequestPending={isRequestPending}
					onCheckBoxChanged={onCheckBoxChanged}
					requestIdsToPick={requestIdsToPick}
					shouldDisplayLocation={shouldDisplayLocation}
				/>
			)}
		</Box>
	);
};
